import { FC, useCallback, useState } from 'react'
import { PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Link from '@mui/material/Link'
import { makeStyles } from 'tss-react/mui'
import { IoAlertCircle } from 'react-icons/io5'
import { toast } from '../../lib/utils'
import { errorColor, primaryIconSize } from '../../lib/styles/universal'

interface InterestStripeSetupProps {
    onSuccess: () => void
    onSkip: () => void
}

export const InterestStripeSetup: FC<InterestStripeSetupProps> = ({
    onSuccess,
    onSkip,
}) => {
    const { classes: localClasses } = useLocalStyles()
    const stripe = useStripe()
    const elements = useElements()
    const [isSubmitting, setIsSubmitting] = useState(false)

    const handleSubmit: React.FormEventHandler = useCallback(
        async (e) => {
            e.preventDefault()
            if (!stripe || !elements) return

            setIsSubmitting(true)
            try {
                await elements.submit()

                const { error } = await stripe.confirmSetup({
                    elements,
                    confirmParams: {
                        return_url: `${window.location.origin}/interest?setup_complete=true`,
                    },
                    redirect: 'if_required',
                })

                if (error) {
                    toast(
                        error.message ?? 'Unable to save payment method.',
                        <IoAlertCircle
                            color={errorColor}
                            size={primaryIconSize}
                        />,
                        'error'
                    )
                } else {
                    onSuccess()
                }
            } catch (err) {
                console.error(err)
                toast(
                    'Something went wrong. Please try again.',
                    <IoAlertCircle color={errorColor} size={primaryIconSize} />,
                    'error'
                )
            } finally {
                setIsSubmitting(false)
            }
        },
        [stripe, elements, onSuccess]
    )

    return (
        <Stack gap="1.5rem" width="100%" sx={{ minWidth: 0 }}>
            <Stack gap="0.5rem">
                <Typography variant="h4" component="h2">
                    Set up billing
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    Add your payment method now so your account can be activated
                    once approved. You won't be charged until your
                    ResiliencePoint account is activated.
                </Typography>
            </Stack>
            <form onSubmit={handleSubmit} className={localClasses.form}>
                <PaymentElement options={{ layout: 'accordion' }} />
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    flexWrap="wrap"
                    gap="1rem"
                    mt="0.5rem"
                >
                    <Link
                        component="button"
                        type="button"
                        variant="body2"
                        onClick={onSkip}
                        underline="hover"
                        color="text.secondary"
                    >
                        Skip for now
                    </Link>
                    <Button
                        variant="contained"
                        type="submit"
                        size="large"
                        disabled={!stripe || !elements || isSubmitting}
                    >
                        Save Payment Method
                    </Button>
                </Stack>
            </form>
        </Stack>
    )
}

const useLocalStyles = makeStyles()({
    form: {
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem',
        width: '100%',
    },
})
